import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { ICONS } from '@/constants/icons';
import { BulkImportError } from '@/types/bulkImport';

/**
 * BulkImportErrors Component
 *
 * Displays row errors and non-fatal import warnings in a sortable table.
 */

interface BulkImportErrorsProps {
  errors: BulkImportError[];
}

type SortField = 'row' | 'field';
type SortDirection = 'asc' | 'desc';

export const BulkImportErrors = ({ errors }: BulkImportErrorsProps) => {
  const { t } = useTranslation('common');
  const [sortField, setSortField] = useState<SortField>('row');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isExpanded, setIsExpanded] = useState(false);

  if (!errors || errors.length === 0) {
    return null;
  }

  const isWarning = (entry: BulkImportError) => /^\[WARNING\](?:\s|$)/.test(entry.message);
  const warningCount = errors.filter(isWarning).length;
  const errorCount = errors.length - warningCount;
  const warningOnly = errorCount === 0;
  const mixed = errorCount > 0 && warningCount > 0;
  const displayField = (field: string) => {
    if (field === 'channels' || field === 'channelIds') return t('bulk_import_field_channels');
    if (field === 'scheduledAt') return t('bulk_import_field_scheduled_at');
    return field;
  };
  const hasVisibleValue = (entry: BulkImportError) => {
    if (isWarning(entry) && (entry.field === 'channels' || entry.field === 'channelIds')) return false;
    return entry.value !== undefined && entry.value !== null && String(entry.value).trim() !== '';
  };

  /**
   * Handle sort
   */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /**
   * Sort errors
   */
  const sortedErrors = [...errors].sort((a, b) => {
    let comparison = 0;

    if (sortField === 'row') {
      comparison = a.row - b.row;
    } else if (sortField === 'field') {
      comparison = a.field.localeCompare(b.field);
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  /**
   * Truncate value
   */
  const truncateValue = (value: unknown): string => {
    const str = String(value ?? '');
    const maxLength = 50;
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
  };

  /**
   * Sanitize CSV cell to prevent CSV injection
   * Prepends single quote to cells starting with =, +, -, @, |, TAB, or CR.
   */
  const sanitizeCSVCell = (value: unknown): string => {
    const str = String(value ?? '');
    const dangerousChars = ['=', '+', '-', '@', '|', '\t', '\r'];

    if (dangerousChars.some(char => str.startsWith(char))) {
      return `'${str}`;
    }

    return str;
  };

  /**
   * Export errors to CSV
   */
  const exportToCSV = () => {
    const headers = [
      t('bulk_import_error_row'),
      t('bulk_import_error_field'),
      warningCount > 0 ? t('bulk_import_report_message') : t('bulk_import_error_message'),
      t('bulk_import_error_value')
    ];
    const rows = errors.map((error) => [
      error.row,
      displayField(error.field),
      isWarning(error)
        ? `${t('bulk_import_warning_label')}: ${error.message.replace(/^\[WARNING\]\s*/, '')}`
        : error.message,
      hasVisibleValue(error) ? error.value : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => {
          const sanitized = sanitizeCSVCell(cell);
          return `"${sanitized.replace(/"/g, '""')}"`;
        }).join(',')
      ),
    ].join('\n');

    // Excel needs the BOM to recognize UTF-8 umlauts and other localized text.
    const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `import-report-${new Date().getTime()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const displayCount = isExpanded ? sortedErrors.length : Math.min(5, sortedErrors.length);
  const hasMore = sortedErrors.length > 5;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Icon name={ICONS.CAUTION} size="sm" color={warningOnly ? 'text-amber-600' : 'text-red-600'} ariaHidden />
          <h3 className="text-sm font-semibold text-gray-900">
            {warningOnly
              ? t('bulk_import_warnings_title', { count: warningCount })
              : mixed
                ? t('bulk_import_report_title', { errors: errorCount, warnings: warningCount })
                : t('bulk_import_errors_title', { count: errorCount })}
          </h3>
        </div>
        <Button variant="secondary" size="sm" onClick={exportToCSV}>
          <Icon name={ICONS.DOWNLOAD} size="sm" ariaHidden />
          {t('bulk_import_export_csv')}
        </Button>
      </div>

      {/* Import report */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="divide-y divide-gray-200 sm:hidden">
          {sortedErrors.slice(0, displayCount).map((error, index) => (
            <div key={index} className={`space-y-2 p-3 ${isWarning(error) ? 'bg-amber-50/50' : 'bg-white'}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold text-gray-700">
                  {t('bulk_import_error_row')} {error.row}
                </span>
                <code className="max-w-full break-all rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
                  {displayField(error.field)}
                </code>
              </div>
              <div className={`break-words text-sm leading-5 ${isWarning(error) ? 'text-amber-800' : 'text-red-600'}`}>
                {isWarning(error) ? (
                  <>
                    <span className="mr-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900">
                      {t('bulk_import_warning_label')}
                    </span>
                    {error.message.replace(/^\[WARNING\]\s*/, '')}
                  </>
                ) : error.message}
              </div>
              {hasVisibleValue(error) && (
                <div className="break-all font-mono text-xs text-gray-500">
                  {t('bulk_import_error_value')}: {truncateValue(error.value)}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* Row # */}
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('row')}
                >
                  <div className="flex items-center gap-1">
                    <span>{t('bulk_import_error_row')}</span>
                    {sortField === 'row' && (
                      <Icon
                        name={sortDirection === 'asc' ? ICONS.ARROW_UP : ICONS.ARROW_DOWN}
                        size="xs"
                        ariaHidden
                      />
                    )}
                  </div>
                </th>

                {/* Field */}
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('field')}
                >
                  <div className="flex items-center gap-1">
                    <span>{t('bulk_import_error_field')}</span>
                    {sortField === 'field' && (
                      <Icon
                        name={sortDirection === 'asc' ? ICONS.ARROW_UP : ICONS.ARROW_DOWN}
                        size="xs"
                        ariaHidden
                      />
                    )}
                  </div>
                </th>

                {/* Error Message */}
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {warningCount > 0 ? t('bulk_import_report_message') : t('bulk_import_error_message')}
                </th>

                {/* Value */}
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {t('bulk_import_error_value')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedErrors.slice(0, displayCount).map((error, index) => (
                <tr key={index} className={isWarning(error) ? 'bg-amber-50/50 hover:bg-amber-50' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {error.row}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    <code className="px-2 py-1 bg-gray-100 rounded text-xs">
                      {displayField(error.field)}
                    </code>
                  </td>
                  <td className={`px-4 py-3 text-sm ${isWarning(error) ? 'text-amber-800' : 'text-red-600'}`}>
                    {isWarning(error) ? (
                      <>
                        <span className="mr-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900">
                          {t('bulk_import_warning_label')}
                        </span>
                        {error.message.replace(/^\[WARNING\]\s*/, '')}
                      </>
                    ) : error.message}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {hasVisibleValue(error) ? (
                      <span className="font-mono text-xs">
                        {truncateValue(error.value)}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Show More/Less Button */}
        {hasMore && (
          <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 text-center">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {isExpanded
                ? t('bulk_import_show_less')
                : warningCount > 0
                  ? t('bulk_import_show_more_rows')
                  : t('bulk_import_show_more_errors', { count: sortedErrors.length - displayCount })}
            </button>
          </div>
        )}
      </div>

      {/* Help Text */}
      <p className="text-xs text-gray-500">
        {warningOnly
          ? t('bulk_import_warning_help_text')
          : mixed
            ? t('bulk_import_mixed_help_text')
            : t('bulk_import_error_help_text')}
      </p>
    </div>
  );
};
